import React, { useEffect, useRef, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { useIsFetching } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormValues, STEP_ID, validation } from './FundAccounts.meta';
import {
  AddAccountSelectionModalWrapper,
  Selection,
} from '../../../../../Profile/Accounts/components/AddAccountSelectionModalWrapper/AddAccountSelectionModalWrapper';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { useNavigation } from '../../hooks/useNavigation';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { VisualPicker } from '../../../../../CommonComponents/OPL/VisualPicker/VisualPicker';
import { formatCurrency } from '../../../../../CommonUtils/formatNumber';
import { AccountDTO, useAccountsQuery } from '../../api/useAccountsQuery';
import { queryKeys } from '../../api/queryKeys';
import { FullPageLoading } from '../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { StepLoading } from '../../components/StepLoading/StepLoading';
import { StepErrorState } from '../../components/StepErrorState/StepErrorState';
import { AccountModalUsageContext } from '../../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';
import { WorkflowDTO } from '../../api/useCharitableGivingQuery';

export const FundAccounts = () => {
  const { isLoading, isError, refetch, data } = useAccountsQuery();

  if (isLoading) {
    return <StepLoading />;
  }

  if (isError) {
    return <StepErrorState onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  return <Content accounts={data} />;
};

interface ContentProps {
  accounts: AccountDTO[];
}

const Content = ({ accounts }: ContentProps) => {
  const isFetchingAccounts = useIsFetching(queryKeys.accountsAll()) > 0;

  const { workflow, setWorkflow, setStep } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({ currentStep: STEP_ID });

  const [isOpenAddAccountModal, setIsOpenAddAccountModal] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      fundAccounts: workflow.data.fundAccounts || [],
    },
    resolver: yupResolver(validation),
  });

  /**
   * Effect to allow auto-selecting newly added accounts
   */
  const fundAccountsRef = useRef(accounts.map(({ id }) => id));
  useEffect(() => {
    if (accounts.length !== fundAccountsRef.current.length) {
      const newFundAccounts = accounts.reduce((acc, { id }) => {
        if (!fundAccountsRef.current.includes(id)) {
          acc.push(id);
        }

        return acc;
      }, [] as string[]);

      const currentFundAccounts = getValues('fundAccounts');

      setValue('fundAccounts', [...currentFundAccounts, ...newFundAccounts]);

      fundAccountsRef.current = accounts.map(({ id }) => id);
    }
  }, [accounts, getValues, setValue]);

  const onSubmit = handleSubmit(values => {
    const newWorkflow: WorkflowDTO = {
      ...workflow,
      data: {
        ...workflow.data,
        fundAccounts: values.fundAccounts,
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const AddAccountButton = (
    <Button variant="link" leftIcon="fa fa-plus" onClick={() => setIsOpenAddAccountModal(true)}>
      Add Account
    </Button>
  );

  return (
    <>
      {isFetchingAccounts && <FullPageLoading />}
      <StepContainer
        alert={(errors.fundAccounts as any)?.message}
        title={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text color="neutral.700" fontSize="h3" fontWeight={500}>
              {workflow.data.givingStrategy === 0
                ? 'Which account(s) will fund the DAF?'
                : 'Which account(s) will fund this goal?'}
            </Text>

            {accounts.length > 0 && AddAccountButton}
          </Box>
        }
        onSubmit={onSubmit}
        footer={
          <StepFooter
            isFirstStep={isFirstStep}
            progress={progress}
            onPrevious={previousStep ? () => setStep(previousStep) : undefined}
          />
        }
      >
        {accounts.length === 0 && (
          <Box
            display="grid"
            style={{ placeItems: 'center' }}
            borderTop="1px solid"
            borderColor="neutral.200"
            pt="32"
            mx={-32}
          >
            {AddAccountButton}
          </Box>
        )}

        {accounts.length > 0 && (
          <Controller
            control={control}
            name="fundAccounts"
            render={({ field: { value: fundAccounts } }) => (
              <Box display="flex" flexDirection="column" mt="16" css="gap: 16px">
                {accounts.map(({ id, name, balance, institution }) => (
                  <VisualPicker
                    key={id}
                    variant="radio"
                    label={name}
                    helperText={institution ?? undefined}
                    rightText={formatCurrency(balance)}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();

                      const {
                        currentTarget: { checked },
                      } = event;

                      if (!checked) {
                        return setValue('fundAccounts', [...fundAccounts, id]);
                      }

                      return setValue(
                        'fundAccounts',
                        fundAccounts.filter(accountId => accountId !== id),
                      );
                    }}
                    checked={fundAccounts.includes(id)}
                  />
                ))}
              </Box>
            )}
          />
        )}
      </StepContainer>

      {isOpenAddAccountModal && (
        <AddAccountSelectionModalWrapper
          initialSelection={Selection.Manual}
          usageContext={AccountModalUsageContext.CharitableGivingWorkflow}
          usageContextOptions={{
            CharitableGivingDonor: workflow.data.donor,
            CharitableGivingStrategy: workflow.data.givingStrategy,
            CharitableGivingKindOfProperty: workflow.data.kindOfProperty,
          }}
          showExternalProviders={false}
          onClose={() => setIsOpenAddAccountModal(false)}
        />
      )}
    </>
  );
};
